import {
  ActionIcon, Autocomplete, Badge, Box, Button, Group, NumberInput, Paper,
  SegmentedControl, Select, Stack, Text, TextInput,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';

import type { SegmentCmp, SegmentNode, SegmentOp } from '../api/types';

/**
 * A visual editor over the same AST the backend compiles.
 *
 * It edits the stored JSON directly rather than keeping a parallel model, so
 * there is nothing to translate and nothing to drift. A predicate written here
 * and a predicate written by hand against the API are the same object.
 */

const CLAUSE_TYPES: Array<{ value: SegmentOp; label: string; hint: string }> = [
  { value: 'trait', label: 'Profile trait', hint: 'Anything you set in traits' },
  { value: 'profile', label: 'Profile field', hint: 'Email, phone, timezone, first seen' },
  { value: 'event', label: 'Did an event', hint: 'Behaviour, optionally counted and windowed' },
  { value: 'device', label: 'Has a device', hint: 'An active push registration' },
  { value: 'consent', label: 'Consent', hint: 'Opted in or out of a category' },
  { value: 'segment', label: 'In another segment', hint: 'Compose segments' },
];

const CMP_OPTIONS: Array<{ value: SegmentCmp; label: string }> = [
  { value: 'eq', label: 'is' },
  { value: 'neq', label: 'is not' },
  { value: 'gt', label: 'is more than' },
  { value: 'gte', label: 'is at least' },
  { value: 'lt', label: 'is less than' },
  { value: 'lte', label: 'is at most' },
  { value: 'contains', label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'in', label: 'is one of' },
  { value: 'not_in', label: 'is not one of' },
  { value: 'exists', label: 'is set' },
  { value: 'not_exists', label: 'is not set' },
];

const PROFILE_FIELDS = [
  'email', 'phone', 'timezone', 'locale', 'external_id', 'first_seen_at', 'last_seen_at',
];

export function SegmentBuilder({
  value,
  onChange,
  eventNames = [],
  segments = [],
  depth = 0,
}: {
  value: SegmentNode;
  onChange: (next: SegmentNode) => void;
  eventNames?: string[];
  segments?: Array<{ id: string; name: string }>;
  depth?: number;
}) {
  const isGroup = value.op === 'and' || value.op === 'or';

  if (value.op === 'not') {
    const child = value.children?.[0] ?? emptyClause();
    return (
      <Paper withBorder p="sm" radius="md" bg="var(--mantine-color-default-hover)">
        <Group justify="space-between" mb="xs">
          <Badge color="red" variant="light">NOT</Badge>
          <ActionIcon
            variant="subtle" color="gray" size="sm"
            onClick={() => onChange(child)}
            aria-label="Remove the NOT"
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
        <SegmentBuilder
          value={child}
          onChange={(next) => onChange({ op: 'not', children: [next] })}
          eventNames={eventNames}
          segments={segments}
          depth={depth + 1}
        />
      </Paper>
    );
  }

  if (isGroup) {
    const children = value.children ?? [];
    return (
      <Paper
        withBorder={depth > 0}
        p={depth > 0 ? 'sm' : 0}
        radius="md"
        bg={depth > 0 ? 'var(--mantine-color-default-hover)' : undefined}
      >
        <Stack gap="xs">
          <Group gap="xs">
            <SegmentedControl
              size="xs"
              value={value.op}
              onChange={(op) => onChange({ ...value, op: op as SegmentOp })}
              data={[
                { value: 'and', label: 'Match ALL' },
                { value: 'or', label: 'Match ANY' },
              ]}
            />
            <Text size="xs" c="dimmed">
              {value.op === 'and' ? 'every condition below must hold' : 'at least one must hold'}
            </Text>
          </Group>

          {children.map((child, i) => (
            <Group key={i} align="flex-start" wrap="nowrap" gap="xs">
              <Box style={{ flex: 1, minWidth: 0 }}>
                <SegmentBuilder
                  value={child}
                  onChange={(next) => {
                    const copy = [...children];
                    copy[i] = next;
                    onChange({ ...value, children: copy });
                  }}
                  eventNames={eventNames}
                  segments={segments}
                  depth={depth + 1}
                />
              </Box>
              <ActionIcon
                variant="subtle" color="red" mt={4}
                onClick={() =>
                  onChange({ ...value, children: children.filter((_, j) => j !== i) })
                }
                aria-label="Remove condition"
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ))}

          <Group gap="xs">
            <Button
              size="xs" variant="light" leftSection={<IconPlus size={14} />}
              onClick={() => onChange({ ...value, children: [...children, emptyClause()] })}
            >
              Condition
            </Button>
            <Button
              size="xs" variant="subtle" leftSection={<IconPlus size={14} />}
              onClick={() =>
                onChange({
                  ...value,
                  children: [...children, { op: 'and', children: [emptyClause()] }],
                })
              }
            >
              Group
            </Button>
            <Button
              size="xs" variant="subtle" color="red"
              onClick={() =>
                onChange({ ...value, children: [...children, { op: 'not', children: [emptyClause()] }] })
              }
            >
              NOT
            </Button>
          </Group>
        </Stack>
      </Paper>
    );
  }

  // A leaf clause.
  return (
    <Paper withBorder p="sm" radius="md">
      <Stack gap="xs">
        <Select
          size="xs"
          data={CLAUSE_TYPES.map((c) => ({ value: c.value, label: c.label }))}
          value={value.op}
          onChange={(op) => onChange(defaultsFor(op as SegmentOp))}
          comboboxProps={{ withinPortal: true }}
        />

        {value.op === 'trait' && (
          <Group gap="xs" wrap="wrap" align="flex-end">
            <TextInput
              size="xs" label="Trait" placeholder="plan" style={{ flex: '1 1 140px' }}
              value={value.path ?? ''}
              onChange={(e) => onChange({ ...value, path: e.currentTarget.value })}
            />
            <Select
              size="xs" label="Type" w={100}
              description="Cast before comparing"
              data={[
                { value: 'string', label: 'Text' },
                { value: 'number', label: 'Number' },
                { value: 'bool', label: 'Yes/no' },
                { value: 'date', label: 'Date' },
              ]}
              value={value.type ?? 'string'}
              onChange={(t) => onChange({ ...value, type: t as SegmentNode['type'] })}
              comboboxProps={{ withinPortal: true }}
            />
            <CmpAndValue value={value} onChange={onChange} />
          </Group>
        )}

        {value.op === 'profile' && (
          <Group gap="xs" wrap="wrap" align="flex-end">
            <Select
              size="xs" label="Field" w={150}
              data={PROFILE_FIELDS}
              value={value.field ?? 'email'}
              onChange={(f) => onChange({ ...value, field: f ?? 'email' })}
              comboboxProps={{ withinPortal: true }}
            />
            <CmpAndValue value={value} onChange={onChange} />
          </Group>
        )}

        {value.op === 'event' && (
          <Group gap="xs" wrap="wrap" align="flex-end">
            <Autocomplete
              size="xs" label="Event" placeholder="checkout_completed"
              style={{ flex: '1 1 180px' }}
              data={eventNames}
              value={value.name ?? ''}
              onChange={(name) => onChange({ ...value, name })}
              comboboxProps={{ withinPortal: true }}
            />
            <Select
              size="xs" label="Count" w={110}
              data={CMP_OPTIONS.filter((c) =>
                ['eq', 'gte', 'lte', 'gt', 'lt'].includes(c.value),
              )}
              value={value.cmp ?? 'gte'}
              onChange={(cmp) => onChange({ ...value, cmp: (cmp ?? 'gte') as SegmentCmp })}
              comboboxProps={{ withinPortal: true }}
            />
            <NumberInput
              size="xs" w={80} min={0} label="Times"
              value={value.count ?? 1}
              onChange={(v) => onChange({ ...value, count: Number(v) || 0 })}
            />
            <TextInput
              size="xs" w={110} label="Within" placeholder="30d"
              description="m, h, d, w"
              value={value.within ?? ''}
              onChange={(e) => onChange({ ...value, within: e.currentTarget.value })}
            />
          </Group>
        )}

        {value.op === 'device' && (
          <Select
            size="xs" label="Platform" w={180} clearable
            placeholder="Any platform"
            data={['ios', 'android', 'web']}
            value={value.platform ?? null}
            onChange={(p) => onChange({ ...value, platform: p ?? undefined })}
            comboboxProps={{ withinPortal: true }}
          />
        )}

        {value.op === 'consent' && (
          <Group gap="xs" wrap="wrap" align="flex-end">
            <Select
              size="xs" label="State" w={130}
              data={[
                { value: 'true', label: 'Opted in' },
                { value: 'false', label: 'Opted out' },
              ]}
              value={String(value.opted_in ?? true)}
              onChange={(v) => onChange({ ...value, opted_in: v === 'true' })}
              comboboxProps={{ withinPortal: true }}
            />
            <Select
              size="xs" label="Category" w={140}
              data={['marketing', 'product', 'billing']}
              value={value.category ?? 'marketing'}
              onChange={(c) => onChange({ ...value, category: c ?? 'marketing' })}
              comboboxProps={{ withinPortal: true }}
            />
            <Select
              size="xs" label="Channel" w={130}
              data={['push', 'email', 'sms', 'whatsapp']}
              value={value.channel ?? 'email'}
              onChange={(c) => onChange({ ...value, channel: c ?? 'email' })}
              comboboxProps={{ withinPortal: true }}
            />
          </Group>
        )}

        {value.op === 'segment' && (
          <Select
            size="xs" label="Segment" searchable
            data={segments.map((s) => ({ value: s.id, label: s.name }))}
            value={value.segment_id ?? null}
            onChange={(id) => onChange({ ...value, segment_id: id ?? undefined })}
            comboboxProps={{ withinPortal: true }}
          />
        )}
      </Stack>
    </Paper>
  );
}

function CmpAndValue({
  value,
  onChange,
}: {
  value: SegmentNode;
  onChange: (next: SegmentNode) => void;
}) {
  const needsValue = value.cmp !== 'exists' && value.cmp !== 'not_exists';
  const isList = value.cmp === 'in' || value.cmp === 'not_in';

  return (
    <>
      <Select
        size="xs" label="Is" w={140}
        data={CMP_OPTIONS}
        value={value.cmp ?? 'eq'}
        onChange={(cmp) => onChange({ ...value, cmp: (cmp ?? 'eq') as SegmentCmp })}
        comboboxProps={{ withinPortal: true }}
      />
      {needsValue && !isList && (
        <TextInput
          size="xs" label="Value" style={{ flex: '1 1 140px' }}
          value={String(value.value ?? '')}
          onChange={(e) => onChange({ ...value, value: e.currentTarget.value })}
        />
      )}
      {isList && (
        <TextInput
          size="xs" label="Values" placeholder="pro, team, enterprise"
          description="Comma-separated"
          style={{ flex: '1 1 200px' }}
          value={(value.values ?? []).join(', ')}
          onChange={(e) =>
            onChange({
              ...value,
              values: e.currentTarget.value.split(',').map((s) => s.trim()).filter(Boolean),
            })
          }
        />
      )}
    </>
  );
}

function emptyClause(): SegmentNode {
  return { op: 'trait', path: '', cmp: 'eq', value: '', type: 'string' };
}

function defaultsFor(op: SegmentOp): SegmentNode {
  switch (op) {
    case 'trait':
      return emptyClause();
    case 'profile':
      return { op: 'profile', field: 'email', cmp: 'exists' };
    case 'event':
      return { op: 'event', name: '', cmp: 'gte', count: 1, within: '30d' };
    case 'device':
      return { op: 'device' };
    case 'consent':
      return { op: 'consent', channel: 'email', category: 'marketing', opted_in: true };
    case 'segment':
      return { op: 'segment', segment_id: '' };
    default:
      return emptyClause();
  }
}

export const EMPTY_DEFINITION: SegmentNode = { op: 'and', children: [emptyClause()] };
